import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import {
  Church, BookOpen, Users, Bell, LogOut, Plus, Loader, Star, ChevronRight,
  Heart, Calendar, Award, MessageCircle, X, Send, ChevronDown, ChevronUp
} from "lucide-react";
import { AvatarDisplay } from "./ParentDashboard";
import { useLanguage } from "../hooks/useLanguage";
import ChurchFamilyOnboarding from "./ChurchFamilyOnboarding";

export default function ChurchFamilyDashboard() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const { t } = useLanguage();
  const [church, setChurch] = useState(null);
  const [children, setChildren] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [churchStudy, setChurchStudy] = useState(null);
  const [prayerRequests, setPrayerRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPrayerModal, setShowPrayerModal] = useState(false);
  const [expandedAnnouncement, setExpandedAnnouncement] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    loadData();
    // Check if user needs onboarding
    if (user && !user.onboarding_complete) {
      setShowOnboarding(true);
    }
  }, [user]);

  const loadData = async () => {
    try {
      const [churchRes, childrenRes, announcementsRes, studyRes, prayerRes] = await Promise.all([
        axios.get(`${API}/church-family/church-info`),
        axios.get(`${API}/children`),
        axios.get(`${API}/church-family/announcements`),
        axios.get(`${API}/church/study/today`),
        axios.get(`${API}/prayer-requests`)
      ]);
      setChurch(churchRes.data);
      setChildren(childrenRes.data);
      setAnnouncements(announcementsRes.data);
      setChurchStudy(studyRes.data);
      setPrayerRequests(prayerRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleOnboardingComplete = async () => {
    try {
      await axios.post(`${API}/users/onboarding-complete`);
      // Update local user state
      if (updateUser) {
        updateUser({ ...user, onboarding_complete: true });
      }
    } catch (e) {
      console.error("Failed to save onboarding status:", e);
    }
    setShowOnboarding(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F0F4FF" }}>
        <Loader size={48} className="animate-spin" color="#118AB2" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ background: "#F0F4FF" }}>
      {/* Header */}
      <div className="px-4 pt-6 pb-12"
        style={{ background: "linear-gradient(135deg, #073B4C 0%, #118AB2 100%)" }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-2xl p-3">
                <Church size={28} color="white" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-2xl font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {church?.name || "Your Church"}
                </h1>
                <p className="text-white/70 text-sm font-medium">
                  Welcome, {user?.name}!
                </p>
              </div>
            </div>
            <button onClick={handleLogout}
              className="bg-white/20 p-2.5 rounded-xl hover:bg-white/30 transition-colors"
              data-testid="logout-btn">
              <LogOut size={20} color="white" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-6">
        {/* Announcements Section */}
        {announcements.length > 0 && (
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-5">
            <div className="p-4 border-b" style={{ background: "linear-gradient(90deg, #EF476F20, #FFD16620)" }}>
              <div className="flex items-center gap-3">
                <Bell size={24} color="#EF476F" strokeWidth={2.5} />
                <div>
                  <h3 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                    Church Announcements
                  </h3>
                  <p className="text-xs text-gray-500">{announcements.length} announcements</p>
                </div>
              </div>
            </div>
            <div className="divide-y max-h-72 overflow-y-auto">
              {announcements.map((ann) => (
                <div key={ann.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-bold text-[#073B4C]">{ann.title}</p>
                        {ann.priority === "important" && (
                          <span className="bg-orange-100 text-orange-600 text-xs px-2 py-0.5 rounded-full font-bold">
                            Important
                          </span>
                        )}
                        {ann.priority === "urgent" && (
                          <span className="bg-red-100 text-red-600 text-xs px-2 py-0.5 rounded-full font-bold">
                            Urgent
                          </span>
                        )}
                      </div>
                      <p className={`text-sm text-gray-600 ${expandedAnnouncement === ann.id ? '' : 'line-clamp-2'}`}>
                        {ann.message}
                      </p>
                      {ann.message.length > 100 && (
                        <button
                          onClick={() => setExpandedAnnouncement(expandedAnnouncement === ann.id ? null : ann.id)}
                          className="text-xs text-[#118AB2] font-bold mt-1"
                        >
                          {expandedAnnouncement === ann.id ? 'Show less' : 'Read more'}
                        </button>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        <Calendar size={10} className="inline mr-1" />
                        {new Date(ann.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Today's Church Study */}
        {churchStudy && (
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-5">
            <div className="p-4 border-b" style={{ background: "linear-gradient(90deg, #9B5DE520, #EF476F20)" }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <BookOpen size={24} color="#9B5DE5" strokeWidth={2.5} />
                  <div>
                    <h3 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                      Today's Church Study
                    </h3>
                    <p className="text-xs text-gray-500">{churchStudy.title}</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate("/church-family/study")}
                  className="bg-[#9B5DE5] text-white font-bold px-4 py-2 rounded-xl text-sm flex items-center gap-1"
                  data-testid="view-study-btn"
                >
                  View <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Children Section */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-5">
          <div className="p-4 border-b flex items-center justify-between" style={{ background: "linear-gradient(90deg, #06D6A020, #118AB220)" }}>
            <div className="flex items-center gap-3">
              <Star size={24} color="#06D6A0" strokeWidth={2.5} />
              <div>
                <h3 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  Your Children
                </h3>
                <p className="text-xs text-gray-500">{children.length} kids</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/church-family/add-child")}
              className="bg-[#06D6A0] text-white font-bold px-3 py-1.5 rounded-xl text-sm flex items-center gap-1"
              data-testid="add-child-btn"
            >
              <Plus size={16} /> Add
            </button>
          </div>
          
          {children.length === 0 ? (
            <div className="p-8 text-center">
              <Users size={40} className="mx-auto mb-2 opacity-40" color="#06D6A0" />
              <p className="text-gray-500 font-medium">No children added yet</p>
              <p className="text-gray-400 text-sm">Add your children to start their Bible journey</p>
            </div>
          ) : (
            <div className="divide-y">
              {children.map((child) => (
                <button
                  key={child.id}
                  onClick={() => navigate(`/child/${child.id}`)}
                  className="w-full p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors text-left"
                  data-testid={`child-${child.id}`}
                >
                  <AvatarDisplay avatar={child.avatar} size={48} />
                  <div className="flex-1">
                    <p className="font-black text-[#073B4C]">{child.name}</p>
                    <p className="text-xs text-gray-500">
                      {child.age_group === "little" ? "Little Light (3-7)" : 
                       child.age_group === "big" ? "Explorer (8-12)" : "Faith Builder (13-18)"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-black text-[#FFD166]">{child.current_streak || 0}</p>
                    <p className="text-xs text-gray-400">day streak</p>
                  </div>
                  <ChevronRight size={20} color="#9CA3AF" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Prayer Requests Section */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-5">
          <div className="p-4 border-b flex items-center justify-between" style={{ background: "linear-gradient(90deg, #F0E6FF, #FFE4E6)" }}>
            <div className="flex items-center gap-3">
              <Heart size={24} color="#EF476F" strokeWidth={2.5} />
              <div>
                <h3 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  Prayer Requests
                </h3>
                <p className="text-xs text-gray-500">{prayerRequests.length} requests</p>
              </div>
            </div>
            <button
              onClick={() => setShowPrayerModal(true)}
              className="bg-[#EF476F] text-white font-bold px-3 py-1.5 rounded-xl text-sm flex items-center gap-1"
              data-testid="new-prayer-btn"
            >
              <Plus size={16} /> New
            </button>
          </div>
          
          {prayerRequests.length === 0 ? (
            <div className="p-8 text-center">
              <Heart size={40} className="mx-auto mb-2 opacity-40" color="#EF476F" />
              <p className="text-gray-500 font-medium">No prayer requests yet</p>
              <p className="text-gray-400 text-sm">Share your prayers with your church</p>
            </div>
          ) : (
            <div className="divide-y max-h-48 overflow-y-auto">
              {prayerRequests.slice(0, 5).map((req) => (
                <div key={req.id} className="p-3 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-sm text-[#073B4C]">{req.title}</p>
                      <p className="text-xs text-gray-400">
                        {req.status === "answered" ? "✓ Answered" : 
                         req.status === "praying" ? "🙏 Being prayed for" : "Pending"}
                      </p>
                    </div>
                    {req.is_important && (
                      <span className="bg-red-100 text-red-600 text-xs px-2 py-0.5 rounded-full font-bold">
                        Important
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => navigate("/family")}
            className="bg-white rounded-2xl p-5 shadow-md flex flex-col items-center gap-2 hover:scale-[1.02] transition-transform"
            data-testid="family-study-btn"
          >
            <div className="rounded-2xl p-3" style={{ background: "#9B5DE520" }}>
              <Users size={28} color="#9B5DE5" strokeWidth={2.5} />
            </div>
            <p className="font-black text-[#073B4C] text-sm" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Family Study
            </p>
          </button>
          
          <button
            onClick={() => navigate("/church-family/leaderboard")}
            className="bg-white rounded-2xl p-5 shadow-md flex flex-col items-center gap-2 hover:scale-[1.02] transition-transform"
            data-testid="leaderboard-btn"
          >
            <div className="rounded-2xl p-3" style={{ background: "#FFD16620" }}>
              <Award size={28} color="#FFD166" strokeWidth={2.5} />
            </div>
            <p className="font-black text-[#073B4C] text-sm" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Leaderboard
            </p>
          </button>
        </div>
      </div>

      {/* Prayer Request Modal */}
      {showPrayerModal && (
        <PrayerRequestModal 
          onClose={() => setShowPrayerModal(false)}
          onSaved={() => {
            setShowPrayerModal(false);
            loadData();
          }}
        />
      )}

      {/* Onboarding Flow */}
      {showOnboarding && (
        <ChurchFamilyOnboarding 
          onComplete={handleOnboardingComplete}
          churchName={church?.name}
        />
      )}
    </div>
  );
}

function PrayerRequestModal({ onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isImportant, setIsImportant] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/prayer-requests`, {
        title,
        description,
        is_important: isImportant
      });
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not submit prayer request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 rounded-xl p-2">
              <MessageCircle size={24} color="#9B5DE5" />
            </div>
            <h2 className="text-xl font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Prayer Request
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#9B5DE5] transition-colors"
              placeholder="e.g., Healing for my grandmother"
              required
              data-testid="prayer-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#9B5DE5] transition-colors resize-none"
              placeholder="Share more details about your prayer request..."
              rows={4}
              required
              data-testid="prayer-description-input"
            />
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isImportant}
              onChange={(e) => setIsImportant(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-[#EF476F] focus:ring-[#EF476F]"
            />
            <span className="text-sm text-gray-600">
              Mark as important/urgent
            </span>
          </label>

          {error && (
            <div className="bg-red-50 text-red-600 text-sm font-medium p-3 rounded-xl">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold py-3 rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !title.trim() || !description.trim()}
              className="flex-1 bg-gradient-to-r from-[#9B5DE5] to-[#EF476F] hover:opacity-90 text-white font-black py-3 rounded-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ fontFamily: "Fredoka, sans-serif" }}
              data-testid="submit-prayer-btn"
            >
              {loading ? "Sending..." : <>
                <Send size={18} /> Submit
              </>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
